import Tooltip from '@components/v2/ui/Tooltip/Tooltip';
import { Box, Button, Typography } from '@mui/material';
import { format } from 'date-fns';
import LangEnum from '../../../../../../lang/LangEnum';
import { ClarityEvents } from '../../../../../../lib/clarity/types/clarityEvents.types';
import { withEvent } from '../../../../../../lib/clarity/utils/captureEvent.utils';
import { OfflineTestIds } from '../../../../../../utils/testIds/Offline.testIds';
import { useTranslation } from '../../../../../translation/TranslationContext';
import { useNetworkContext } from '../../../../contexts/network/network.context';
import useCanEnterInOfflineMode from '../../../../hooks/useCanEnterInOfflineMode';
import OfflineModalProps from '../../offlineModal.types';
import { useOfflineModalContentStyles } from './offlineModalContent.styles';

type OfflineModalContentProps = Pick<
  OfflineModalProps,
  'downloadData' | 'hasSyncedData' | 'lastSyncedAt' | 'removeOfflineData' | 'updateData'
>;

const OfflineModalContent = ({
  updateData,
  downloadData,
  lastSyncedAt,
  hasSyncedData,
  removeOfflineData,
}: OfflineModalContentProps) => {
  const { classes } = useOfflineModalContentStyles();
  const { translate } = useTranslation();
  const { canEnterInOfflineMode } = useCanEnterInOfflineMode();
  const { setIsForcingOfflineMode } = useNetworkContext();

  const handleSetForceOfflineMode = () => {
    setIsForcingOfflineMode({ forceOfflineMode: true });
  };

  return (
    <Box
      className={classes.wrapper}
      sx={{
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '8px',
        }}
      >
        {hasSyncedData ? (
          <Button
            variant="contained"
            className={classes.customButtonBackground}
            onClick={withEvent(ClarityEvents.UPDATE_OFFLINE_DATA, updateData)}
          >
            <Typography variant="h6" color="textPrimary">
              {translate(LangEnum.GLOBAL_UPDATE_PRODUCTS_DATA)}
            </Typography>
          </Button>
        ) : (
          <Button
            test-id={OfflineTestIds.DOWNLOAD_DATA_BUTTON}
            variant="contained"
            onClick={withEvent(ClarityEvents.DOWNLOAD_OFFLINE_DATA, downloadData)}
            color="primary"
          >
            <Typography variant="h6">
              {translate(LangEnum.GLOBAL_DOWNLOAD_PRODUCTS_DATA)}
            </Typography>
          </Button>
        )}

        {hasSyncedData && (
          <>
            <Button
              variant="contained"
              onClick={removeOfflineData}
              className={classes.customButtonBackground}
            >
              <Typography variant="h6" color="textPrimary">
                {translate(LangEnum.GLOBAL_DELETE_DOWNLOADED_DATA)}
              </Typography>
            </Button>

            <Tooltip
              TooltipContent={
                !canEnterInOfflineMode &&
                translate(LangEnum.OFFLINE_UPDATE_PRODUCTS_DATA_BEFORE_ENTERING_OFFLINE_MODE)
              }
            >
              <Button
                test-id={OfflineTestIds.ENTER_OFFLINE_BUTTON}
                variant="contained"
                color="primary"
                fullWidth
                disabled={!canEnterInOfflineMode}
                onClick={withEvent(ClarityEvents.ENTER_OFFLINE_MODE, handleSetForceOfflineMode)}
              >
                <Typography variant="h6">
                  {translate(LangEnum.GLOBAL_ENTER_OFFLINE_MODE)}
                </Typography>
              </Button>
            </Tooltip>
          </>
        )}
      </Box>
      {hasSyncedData && (
        <Typography variant="body2" color="textSecondary">
          {translate(LangEnum.OFFLINE_DOWNLOAD_MODAL_UPDATED_AT_TEXT, {
            date: lastSyncedAt ? format(lastSyncedAt, 'dd/MM/yyyy HH:mm') : null,
          })}
        </Typography>
      )}
    </Box>
  );
};

export default OfflineModalContent;
